'use client';
import { useState } from 'react';
 import AppImage from'@/components/ui/AppImage';
import CheckoutModal from '@/components/checkout/CheckoutModal';

interface HeroSectionProps {
  productName: string
  tagline: string
  description: string
  price: string
  unitsRemaining: number
  totalUnits: number
  imageUrl: string
  imageAlt: string
}

export default function HeroSection({
  productName,
  tagline,
  description,
  price,
  unitsRemaining,
  totalUnits,
  imageUrl,
  imageAlt
}: HeroSectionProps) {
  const [mousePosition, setMousePosition] = useState({ x: '50%', y: '50%' })
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    setMousePosition({ x: `${x}%`, y: `${y}%` })
  }

  return (
    <>
    <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 py-16 sm:py-20 md:py-32">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary via-background to-accent/20 -z-10" />
      
      <div className="max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
          
          {/* Left: Product Image with Spotlight */}
          <div 
            className="relative spotlight-card rounded-xl sm:rounded-2xl overflow-hidden aspect-square bg-card/50 backdrop-blur-sm grid-border"
            onMouseMove={handleMouseMove}
            style={{
              '--mouse-x': mousePosition.x,
              '--mouse-y': mousePosition.y,
            } as React.CSSProperties}
          >
            <div className="relative z-10 p-6 sm:p-8 md:p-12 flex items-center justify-center h-full">
              <AppImage
                src={imageUrl}
                alt={imageAlt}
                className="w-full h-full object-contain"
                priority
              />
            </div>
          </div>

          {/* Right: Content */}
          <div className="space-y-6 sm:space-y-8">
            {/* Header */}
            <div className="space-y-3 sm:space-y-4">
              <h1 className="playfair-header text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-foreground leading-tight">
                {productName}
              </h1>
              <p className="text-xl sm:text-2xl md:text-3xl text-foreground/80 font-light">
                {tagline}
              </p>
            </div>

            {/* Description */}
            <p className="text-base sm:text-lg text-foreground/70 leading-relaxed max-w-xl">
              {description}
            </p>

            {/* Price & CTA */}
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="mono-data text-sm sm:text-base font-bold text-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Total Price: {price}
                </div>
                <div className="mono-data text-xs uppercase tracking-wider text-foreground/60">
                  (No hidden fees)
                </div>
                <div className="mono-data text-xs sm:text-sm uppercase tracking-wider text-foreground/60">
                  BATCH 001 STATUS: {unitsRemaining}/{totalUnits} UNITS REMAINING
                </div>
              </div>
              
              <button
                onClick={() => setIsCheckoutOpen(true)}
                className="inline-block w-full md:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-primary text-primary-foreground mono-data text-xs sm:text-sm uppercase tracking-wider rounded-lg grid-border hover:bg-primary/90 transition-all duration-300 text-center font-semibold cursor-pointer"
              >
                INITIALISE SECURE CHECKOUT — {price}
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-3 sm:gap-4 pt-4 border-t border-border/30">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/60">
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="mono-data">SECURE PAYMENT</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
    <CheckoutModal
      isOpen={isCheckoutOpen}
      onClose={() => setIsCheckoutOpen(false)}
      productName={productName}
      price={38.88}
      currency="gbp"
    />
    </>
  )
}